/**
 * Quiz utilities for generating and managing quiz data
 */

// Generate a quiz based on a topic
// count: preferred number of questions (defaults to 5)
// difficulty: 'easy' | 'medium' | 'hard' | 'mixed' (informational only for local generation)
export const generateQuiz = (topic, count = 5, difficulty = 'mixed') => {
  // In a real application, this would fetch from an API
  // For now, we'll generate some sample quizzes based on the topic
  
  const lowerTopic = topic.toLowerCase();
  
  // JavaScript quiz
  if (lowerTopic.includes('javascript') || lowerTopic.includes('js')) {
    return {
      id: `quiz-js-${Date.now()}`,
      title: 'JavaScript Fundamentals Quiz',
      description: 'Test your knowledge of JavaScript fundamentals',
      cards: [
        {
          id: 'js-1',
          question: 'What is the output of: console.log(typeof null)?',
          answer: 'object',
          options: ['string', 'object', 'null', 'undefined'],
          explanation: 'In JavaScript, typeof null returns "object", which is considered a bug in the language.'
        },
        {
          id: 'js-2',
          question: 'What does the "use strict" directive do in JavaScript?',
          answer: 'Enables strict mode',
          options: ['Enables strict mode', 'Disables browser caching', 'Increases performance', 'Enables ES6 features'],
          explanation: 'Strict mode enforces stricter parsing and error handling, prevents using undeclared variables, and more.'
        },
        {
          id: 'js-3',
          question: 'What is the difference between == and === operators?',
          answer: '== compares values with type conversion, === compares values and types without conversion',
          options: [
            '== compares values with type conversion, === compares values and types without conversion', 
            'Both operators work exactly the same way', 
            '== is faster than ===', 
            '=== is only used for comparing objects'
          ],
          explanation: 'The == operator performs type coercion if needed, while === doesn\'t perform type coercion and returns false for different types.'
        },
        {
          id: 'js-4',
          question: 'What is a closure in JavaScript?',
          answer: 'A function that has access to variables from its outer (enclosing) scope',
          options: [
            'A function that has access to variables from its outer (enclosing) scope', 
            'A way to close browser windows using JavaScript', 
            'A method to terminate event listeners', 
            'A technique for hiding private variables'
          ],
          explanation: 'Closures are created when a function is defined within another function, allowing it to access the outer function\'s variables.'
        },
        {
          id: 'js-5',
          question: 'What is the event loop in JavaScript?',
          answer: 'A mechanism that handles asynchronous callbacks and executes code',
          options: [
            'A mechanism that handles asynchronous callbacks and executes code', 
            'A UI element that shows loading progress', 
            'A method for creating infinite loops', 
            'A data structure for storing events'
          ],
          explanation: 'The event loop continuously checks the call stack and callback queue, moving callbacks to the stack when it\'s empty.'
        }
      ].slice(0, Math.max(3, Math.min(25, Number.isFinite(count) ? count : 5)))
    };
  }

  // C/C++ quiz
  else if (lowerTopic.includes('c++') || lowerTopic === 'c' || /\bc language\b/.test(lowerTopic)) {
    const title = lowerTopic.includes('c++') ? 'C++ Concepts Quiz' : 'C Language Fundamentals Quiz';
    return {
      id: `quiz-cpp-${Date.now()}`,
      title,
      description: 'Test core language rules, syntax, and idioms',
      cards: [
        {
          id: 'cpp-1',
          question: lowerTopic.includes('c++') ? 'What is RAII in C++?' : 'What does the const keyword do in C?',
          answer: lowerTopic.includes('c++') ? 'Resource Acquisition Is Initialization' : 'Prevents modification of the qualified object through that identifier',
          options: lowerTopic.includes('c++') ? [
            'Resource Acquisition Is Initialization',
            'Run-time Automatic Interface Invocation',
            'Random Access Inline Initialization',
            'Reference-Adjusted Instance Initialization'
          ] : [
            'Prevents modification of the qualified object through that identifier',
            'Allocates memory on the heap',
            'Marks a function for inlining',
            'Creates a pointer to volatile memory'
          ],
          explanation: lowerTopic.includes('c++') ? 'RAII ties resource lifetime to object lifetime; resources are released in destructors.' : 'const objects cannot be modified via that name; applies to pointers and pointees depending on placement.'
        },
        {
          id: 'cpp-2',
          question: lowerTopic.includes('c++') ? 'Which is true about move semantics (C++11+)?' : 'Which of the following about pointers in C is correct?',
          answer: lowerTopic.includes('c++') ? 'They enable transferring resources without deep copy using rvalue references' : 'Dereferencing a NULL pointer leads to undefined behavior',
          options: lowerTopic.includes('c++') ? [
            'They enable transferring resources without deep copy using rvalue references',
            'They automatically make all copies thread-safe',
            'They eliminate the need for destructors',
            'They are equivalent to copy semantics'
          ] : [
            'Dereferencing a NULL pointer leads to undefined behavior',
            'sizeof(int*) equals sizeof(int) on all platforms',
            'Pointer arithmetic is defined for any arbitrary pointer',
            'A dangling pointer is safe to dereference'
          ],
          explanation: lowerTopic.includes('c++') ? 'Move semantics optimize transfers by moving resources from temporaries via rvalue refs.' : 'Null dereference is UB; pointer sizes vary; pointer arithmetic restricted to arrays.'
        },
        {
          id: 'cpp-3',
          question: lowerTopic.includes('c++') ? 'What does std::unique_ptr enforce?' : 'What is the correct signature of main in hosted C?',
          answer: lowerTopic.includes('c++') ? 'Exclusive ownership of a dynamically allocated object' : 'int main(void) or int main(int argc, char** argv)',
          options: lowerTopic.includes('c++') ? [
            'Exclusive ownership of a dynamically allocated object',
            'Shared reference-counted ownership',
            'Copyable smart pointer semantics',
            'Automatic stack allocation only'
          ] : [
            'int main(void) or int main(int argc, char** argv)',
            'void main()',
            'int main()',
            'main() returns no value'
          ],
          explanation: lowerTopic.includes('c++') ? 'unique_ptr cannot be copied; it models exclusive ownership and deletes in its destructor.' : 'The C standard specifies these forms for hosted implementations.'
        },
        {
          id: 'cpp-4',
          question: lowerTopic.includes('c++') ? 'What problem do header include guards or #pragma once solve?' : 'Which storage class specifier declares internal linkage by default?',
          answer: lowerTopic.includes('c++') ? 'Preventing multiple inclusion of the same header' : 'static for file-scope identifiers',
          options: lowerTopic.includes('c++') ? [
            'Preventing multiple inclusion of the same header',
            'Enforcing ODR for templates',
            'Preventing name mangling',
            'Generating debug symbols'
          ] : [
            'static for file-scope identifiers',
            'extern inside a function',
            'register for parameters',
            'auto at file scope'
          ],
          explanation: lowerTopic.includes('c++') ? 'Include guards or pragma once avoid duplicate declarations from repeated includes.' : 'static at file scope gives internal linkage.'
        },
        {
          id: 'cpp-5',
          question: lowerTopic.includes('c++') ? 'Which notation declares a pure virtual function?' : 'Which library function safely copies strings with size awareness?',
          answer: lowerTopic.includes('c++') ? 'virtual void f() = 0;' : 'strncpy (with care about null-termination)',
          options: lowerTopic.includes('c++') ? [
            'virtual void f() = 0;',
            'virtual void f() {};',
            'void f() override final;',
            'virtual ~f();'
          ] : [
            'strncpy (with care about null-termination)',
            'gets',
            'strcpy (always safe)',
            'scanf without field widths'
          ],
          explanation: lowerTopic.includes('c++') ? '“= 0” makes a function pure virtual.' : 'gets is unsafe; strcpy can overflow; strncpy requires careful usage.'
        }
      ].slice(0, Math.max(3, Math.min(25, Number.isFinite(count) ? count : 5)))
    };
  }

  // Java quiz
  else if (lowerTopic.includes('java')) {
    return {
      id: `quiz-java-${Date.now()}`,
      title: 'Java Core Concepts Quiz',
      description: 'Test OOP, JVM, and core library knowledge',
      cards: [
        {
          id: 'java-1',
          question: 'Which statement about the JVM is correct?',
          answer: 'It executes Java bytecode and provides platform independence',
          options: [
            'It executes Java bytecode and provides platform independence',
            'It compiles Java directly to native machine code without bytecode',
            'It is only available on Linux',
            'It cannot perform garbage collection'
          ],
          explanation: 'The JVM interprets/compiles bytecode; GC is a key feature.'
        },
        {
          id: 'java-2',
          question: 'Which is NOT a Java primitive type?',
          answer: 'String',
          options: ['int', 'double', 'boolean', 'String'],
          explanation: 'String is a class in java.lang.'
        },
        {
          id: 'java-3',
          question: 'What does the final keyword on a variable indicate?',
          answer: 'Its reference cannot be reassigned after initialization',
          options: [
            'Its reference cannot be reassigned after initialization',
            'The object becomes immutable',
            'It enables method overloading',
            'It forces the variable onto the stack'
          ],
          explanation: 'final prevents reassignment; object mutability is separate.'
        },
        {
          id: 'java-4',
          question: 'Which collection maintains insertion order and allows duplicates?',
          answer: 'ArrayList',
          options: ['HashSet', 'TreeSet', 'ArrayList', 'HashMap'],
          explanation: 'ArrayList preserves order and allows duplicates.'
        },
        {
          id: 'java-5',
          question: 'Which feature is introduced in Java 8 for functional-style operations?',
          answer: 'Streams API',
          options: ['Servlets', 'RMI', 'Streams API', 'AWT'],
          explanation: 'Streams provide map/filter/reduce style processing.'
        }
      ].slice(0, Math.max(3, Math.min(25, Number.isFinite(count) ? count : 5)))
    };
  }

  // Python quiz
  else if (lowerTopic.includes('python')) {
    return {
      id: `quiz-python-${Date.now()}`,
      title: 'Python Fundamentals Quiz',
      description: 'Syntax, data structures, and common idioms',
      cards: [
        { id: 'py-1', question: 'What does list comprehension produce?', answer: 'A new list', options: ['A new list', 'A generator only', 'A tuple', 'A dict'], explanation: '[] creates a list; () with comprehensions is a generator.' },
        { id: 'py-2', question: 'What is PEP 8?', answer: 'Python style guide', options: ['Async framework', 'Package manager', 'Python style guide', 'Debugger'], explanation: 'PEP 8 describes code style conventions.' },
        { id: 'py-3', question: 'What is the output of len({1,1,2})?', answer: '2', options: ['1', '2', '3', 'Error'], explanation: 'Sets are unique; {1,1,2} -> {1,2}.' },
        { id: 'py-4', question: 'What does the with statement manage?', answer: 'Context managers for setup/teardown', options: ['Threads', 'Only files', 'Context managers for setup/teardown', 'Global state'], explanation: 'with uses __enter__/__exit__ for resources.' },
        { id: 'py-5', question: 'What does *args represent?', answer: 'Variable positional arguments', options: ['Variable keyword arguments', 'Variable positional arguments', 'Type annotations', 'Decorators'], explanation: '*args bundles positional args; **kwargs for keyword.' }
      ].slice(0, Math.max(3, Math.min(25, Number.isFinite(count) ? count : 5)))
    };
  }

  // Earth Science quiz
  else if (lowerTopic.includes('earth science') || lowerTopic.includes('geology') || lowerTopic.includes('meteorology')) {
    return {
      id: `quiz-earth-${Date.now()}`,
      title: 'Earth Science Quiz',
      description: 'Plate tectonics, rock cycle, atmosphere, and geologic processes',
      cards: [
        { id: 'earth-1', question: 'What drives plate tectonics at large scales?', answer: 'Mantle convection and slab pull', options: ['Mantle convection and slab pull', 'Tidal forces from the Moon', 'Solar wind pressure', 'Only earthquakes'], explanation: 'Convection, slab pull, and ridge push are primary drivers.' },
        { id: 'earth-2', question: 'Which rock type forms from cooled magma or lava?', answer: 'Igneous', options: ['Igneous', 'Sedimentary', 'Metamorphic', 'Foliated'], explanation: 'Igneous rocks form from solidified melt.' },
        { id: 'earth-3', question: 'The majority of Earth’s atmosphere is composed of:', answer: 'Nitrogen (~78%)', options: ['Oxygen (~21%)', 'Nitrogen (~78%)', 'Carbon dioxide (~0.04%)', 'Argon (~0.93%)'], explanation: 'Nitrogen is the most abundant gas in the atmosphere.' },
        { id: 'earth-4', question: 'What process transforms sediments into sedimentary rock?', answer: 'Lithification (compaction and cementation)', options: ['Sublimation', 'Lithification (compaction and cementation)', 'Oxidation only', 'Metamorphism only'], explanation: 'Lithification turns loose sediment to rock.' },
        { id: 'earth-5', question: 'Which boundary is associated with subduction?', answer: 'Convergent (oceanic-continental or oceanic-oceanic)', options: ['Divergent', 'Transform', 'Convergent (oceanic-continental or oceanic-oceanic)', 'No boundaries'], explanation: 'Subduction occurs at convergent margins.' }
      ].slice(0, Math.max(3, Math.min(25, Number.isFinite(count) ? count : 5)))
    };
  }

  // React quiz
  else if (lowerTopic.includes('react')) {
    return {
      id: `quiz-react-${Date.now()}`,
      title: 'React Concepts Quiz',
      description: 'Test your knowledge of React fundamentals',
      cards: [
        {
          id: 'react-1',
          question: 'What is JSX in React?',
          answer: 'A syntax extension that allows writing HTML-like code in JavaScript',
          options: [
            'A syntax extension that allows writing HTML-like code in JavaScript',
            'A CSS preprocessor for styling React components',
            'A data fetching library for React',
            'A testing framework for React'
          ],
          explanation: 'JSX makes it easier to write and understand the structure of UI components in React.'
        },
        {
          id: 'react-2',
          question: 'What is the virtual DOM in React?',
          answer: 'A lightweight copy of the real DOM that React uses for performance optimization',
          options: [
            'A lightweight copy of the real DOM that React uses for performance optimization',
            'A server-side rendering engine',
            'A browser API for animations',
            'A database used by React'
          ],
          explanation: 'React creates a virtual representation of the UI, compares it with the previous version, and updates only what has changed.'
        },
        {
          id: 'react-3',
          question: 'What are hooks in React?',
          answer: 'Functions that let you use state and other React features in functional components',
          options: [
            'Functions that let you use state and other React features in functional components',
            'Special HTML attributes used by React',
            'Built-in CSS modules for React',
            'A routing mechanism for single-page apps'
          ],
          explanation: 'Hooks were introduced in React 16.8 to allow using state and lifecycle features without writing classes.'
        },
        {
          id: 'react-4',
          question: 'What is the purpose of the useEffect hook?',
          answer: 'To perform side effects in functional components',
          options: [
            'To perform side effects in functional components',
            'To create context providers',
            'To memoize values across renders',
            'To style components with CSS-in-JS'
          ],
          explanation: 'useEffect handles operations like data fetching, subscriptions, and DOM manipulations that would typically be in lifecycle methods.'
        },
        {
          id: 'react-5',
          question: 'What is the key prop in React lists?',
          answer: 'A special attribute to help React identify which items have changed',
          options: [
            'A special attribute to help React identify which items have changed',
            'A property that encrypts list data',
            'A style attribute used for list items',
            'A configuration for React DevTools'
          ],
          explanation: 'Keys help React identify which elements have been added, removed, or modified, improving rendering performance.'
        }
      ]
    };
  }

  // CSS quiz
  else if (lowerTopic.includes('css') || lowerTopic.includes('style')) {
    return {
      id: `quiz-css-${Date.now()}`,
      title: 'CSS Concepts Quiz',
      description: 'Test your knowledge of CSS fundamentals',
      cards: [
        {
          id: 'css-1',
          question: 'What does CSS stand for?',
          answer: 'Cascading Style Sheets',
          options: [
            'Cascading Style Sheets',
            'Creative Style System',
            'Cascaded Styling Syntax',
            'Computed Style Source'
          ],
          explanation: 'CSS is a style sheet language used for describing the presentation of a document written in HTML.'
        },
        {
          id: 'css-2',
          question: 'What is the box model in CSS?',
          answer: 'A layout concept that describes how elements are rendered with content, padding, border, and margin',
          options: [
            'A layout concept that describes how elements are rendered with content, padding, border, and margin',
            'A color scheme used for styling components',
            'A JavaScript library for CSS animations',
            'A server-side rendering technique'
          ],
          explanation: 'The box model is fundamental to layout in CSS, defining how space is calculated around elements.'
        },
        {
          id: 'css-3',
          question: 'What is the difference between position: relative and position: absolute?',
          answer: 'Relative positions an element relative to its normal position; absolute removes it from the flow and positions it relative to its nearest positioned ancestor',
          options: [
            'Relative positions an element relative to its normal position; absolute removes it from the flow and positions it relative to its nearest positioned ancestor',
            'Relative removes the element from the flow; absolute keeps it in the flow',
            'Both position values behave identically',
            'Absolute can only be used within grid containers'
          ],
          explanation: 'Position relative keeps the element in the document flow, while absolute takes it out of the flow.'
        },
        {
          id: 'css-4',
          question: 'What are CSS media queries used for?',
          answer: 'To apply different styles based on device characteristics like screen size',
          options: [
            'To apply different styles based on device characteristics like screen size',
            'To create animations for interactive elements',
            'To reset default browser styles',
            'To preload images for performance'
          ],
          explanation: 'Media queries are a key component of responsive design, allowing websites to adapt to different devices.'
        },
        {
          id: 'css-5',
          question: 'What is the difference between display: none and visibility: hidden?',
          answer: 'display: none removes the element from the document flow; visibility: hidden hides it but keeps its space',
          options: [
            'display: none removes the element from the document flow; visibility: hidden hides it but keeps its space',
            'Both remove the element from the document flow',
            'Both keep the element visible but transparent',
            'visibility: hidden removes the element; display: none keeps its space'
          ],
          explanation: 'Elements with display: none don\'t take up space, while visibility: hidden elements are invisible but still occupy space.'
        }
      ]
    };
  }
  
  // Default: dynamically generate topic-specific quiz
  else {
    const n = Math.max(3, Math.min(25, Number.isFinite(count) ? count : 5));
    const topicTitle = topic.charAt(0).toUpperCase() + topic.slice(1);

    const templates = [
      (i) => ({
        id: `${lowerTopic}-q${i+1}`,
        question: `Which statement best describes ${topic}?`,
        answer: `${topicTitle} is related to the concept or subject of ${topic}.`,
        options: [
          `${topicTitle} is related to the concept or subject of ${topic}.`,
          `It is the capital city unrelated to ${topic}.`,
          `It refers to a famous novelist, not ${topic}.`,
          `It is a programming loop structure named ${topic}.`
        ],
        explanation: `This question checks basic understanding of ${topic}.`
      }),
      (i) => ({
        id: `${lowerTopic}-q${i+1}`,
        question: `A core idea in ${topic} is which of the following?`,
        answer: `Foundational principles of ${topic}.`,
        options: [
          `Foundational principles of ${topic}.`,
          `The mating habits of penguins.`,
          `The GDP of a random country.`,
          `The syntax of an unrelated programming language.`
        ],
        explanation: `Focus on ideas central to ${topic}.`
      }),
      (i) => ({
        id: `${lowerTopic}-q${i+1}`,
        question: `In the context of ${topic}, which option is most accurate?`,
        answer: `An accurate statement that pertains to ${topic}.`,
        options: [
          `An accurate statement that pertains to ${topic}.`,
          `A misleading fact not about ${topic}.`,
          `An unrelated historical date.`,
          `A random chemistry symbol.`
        ],
        explanation: `Choose the option that actually concerns ${topic}.`
      })
    ];

    const cards = Array.from({ length: n }, (_, i) => {
      const tpl = templates[i % templates.length];
      return tpl(i);
    });

    return {
      id: `quiz-${lowerTopic}-${Date.now()}`,
      title: `${topicTitle} Quiz (${difficulty})`,
      description: `Auto-generated practice on ${topicTitle}.`,
      cards
    };
  }
};

// Check if a message is requesting a quiz
export const isQuizRequest = (message) => {
  const lowerMessage = message.toLowerCase();
  
  // Check for quiz-related keywords (excluding flashcard keywords)
  const quizKeywords = [
    'quiz me',
    'test me',
    'create a quiz',
    'make a quiz',
    'start a quiz',
    'quiz about',
    'test my knowledge',
    'multiple choice'
  ];
  
  return quizKeywords.some(keyword => lowerMessage.includes(keyword));
};

// Check if a message is requesting flashcards
export const isFlashcardRequest = (message) => {
  const lowerMessage = message.toLowerCase();
  
  // Check for flashcard-related keywords
  const flashcardKeywords = [
    'flashcards',
    'flash cards',
    'study cards',
    'make flashcards',
    'create flashcards',
    'flashcard set'
  ];
  
  return flashcardKeywords.some(keyword => lowerMessage.includes(keyword));
};

// Extract topic from a quiz request
export const extractQuizTopic = (message) => {
  const lowerMessage = message.toLowerCase();
  
  // Check for topic patterns
  const patterns = [
    /quiz\s*me\s*(?:about|on)\s+([\w\s,\-()]+)/i,
    /test\s*me\s*(?:about|on)\s+([\w\s,\-()]+)/i,
    /create\s*a\s*quiz\s*(?:about|on)\s+([\w\s,\-()]+)/i, // handles 'create a quizon'
    /make\s*a\s*quiz\s*(?:about|on)\s+([\w\s,\-()]+)/i,
    /start\s*a\s*quiz\s*(?:about|on)\s+([\w\s,\-()]+)/i,
    /flashcards\s*(?:about|on)\s+([\w\s,\-()]+)/i,
    /flash\s*cards\s*(?:about|on)\s+([\w\s,\-()]+)/i,
    /quiz\s*about\s+([\w\s,\-()]+)/i,
    /test\s*my\s*knowledge\s*(?:about|on|of)\s+([\w\s,\-()]+)/i
  ];
  
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match && match[1]) {
      // Clean up trailing punctuation and qualifiers like commas
      const raw = match[1].trim();
      const cleaned = raw.replace(/[.,;:!?)]+$/g, '').trim();
      return cleaned;
    }
  }
  
  // Default topics based on keywords
  if (lowerMessage.includes('javascript') || lowerMessage.includes('js')) {
    return 'javascript';
  } else if (lowerMessage.includes('react')) {
    return 'react';
  } else if (lowerMessage.includes('css') || lowerMessage.includes('style')) {
    return 'css';
  }
  
  // Default to general knowledge
  return 'general knowledge';
};

// Generate flashcards based on a topic
export const generateFlashcards = (topic) => {
  // In a real application, this would fetch from an API
  // For now, we'll generate some sample flashcards based on the topic
  
  const lowerTopic = topic.toLowerCase();
  
  // JavaScript flashcards
  if (lowerTopic.includes('javascript') || lowerTopic.includes('js')) {
    return {
      id: `flashcards-js-${Date.now()}`,
      title: 'JavaScript Fundamentals Flashcards',
      description: 'Review key JavaScript concepts with these flashcards',
      cards: [
        {
          id: 'js-fc-1',
          question: 'What is a closure in JavaScript?',
          answer: 'A function that has access to variables from its outer (enclosing) scope, even after the outer function has returned.',
          explanation: 'Closures are commonly used to create private variables and for function factories.'
        },
        {
          id: 'js-fc-2',
          question: 'What is the difference between let, const, and var?',
          answer: 'var is function-scoped and hoisted, let is block-scoped, and const is block-scoped and cannot be reassigned.',
          explanation: 'let and const were introduced in ES6 to address issues with var.'
        },
        {
          id: 'js-fc-3',
          question: 'What is a Promise in JavaScript?',
          answer: 'An object representing the eventual completion or failure of an asynchronous operation.',
          explanation: 'Promises have three states: pending, fulfilled, and rejected.'
        },
        {
          id: 'js-fc-4',
          question: 'What is the event loop?',
          answer: 'A mechanism that allows JavaScript to perform non-blocking operations despite being single-threaded.',
          explanation: 'The event loop continuously checks the call stack and callback queue.'
        },
        {
          id: 'js-fc-5',
          question: 'What is prototypal inheritance?',
          answer: 'A method by which an object can inherit properties and methods from another object.',
          explanation: 'JavaScript uses prototype-based inheritance instead of class-based inheritance.'
        }
      ]
    };
  }
  
  // React flashcards
  else if (lowerTopic.includes('react')) {
    return {
      id: `flashcards-react-${Date.now()}`,
      title: 'React Concepts Flashcards',
      description: 'Review key React concepts with these flashcards',
      cards: [
        {
          id: 'react-fc-1',
          question: 'What is JSX?',
          answer: 'A syntax extension for JavaScript that looks similar to HTML and allows you to write HTML in React.',
          explanation: 'JSX is transformed into JavaScript function calls that create React elements.'
        },
        {
          id: 'react-fc-2',
          question: 'What is the virtual DOM?',
          answer: 'A lightweight copy of the real DOM that React uses for performance optimization.',
          explanation: 'React compares the virtual DOM with the real DOM to determine what needs to be updated.'
        },
        {
          id: 'react-fc-3',
          question: 'What are React hooks?',
          answer: 'Functions that let you use state and other React features in functional components.',
          explanation: 'Common hooks include useState, useEffect, useContext, useReducer, and useRef.'
        },
        {
          id: 'react-fc-4',
          question: 'What is the purpose of the key prop in lists?',
          answer: 'To help React identify which items have changed, been added, or been removed.',
          explanation: 'Keys should be unique among siblings and stable across renders.'
        },
        {
          id: 'react-fc-5',
          question: 'What is the Context API used for?',
          answer: 'To share data that can be considered global for a tree of React components.',
          explanation: 'Context provides a way to pass data through the component tree without having to pass props down manually.'
        }
      ]
    };
  }
  
  // CSS flashcards
  else if (lowerTopic.includes('css') || lowerTopic.includes('style')) {
    return {
      id: `flashcards-css-${Date.now()}`,
      title: 'CSS Concepts Flashcards',
      description: 'Review key CSS concepts with these flashcards',
      cards: [
        {
          id: 'css-fc-1',
          question: 'What is the CSS box model?',
          answer: 'A layout concept that describes how elements are rendered with content, padding, border, and margin.',
          explanation: 'The box model is fundamental to layout in CSS.'
        },
        {
          id: 'css-fc-2',
          question: 'What is the difference between position: relative and position: absolute?',
          answer: 'Relative positions an element relative to its normal position; absolute removes it from the flow and positions it relative to its nearest positioned ancestor.',
          explanation: 'Position relative keeps the element in the document flow, while absolute takes it out of the flow.'
        },
        {
          id: 'css-fc-3',
          question: 'What are CSS media queries?',
          answer: 'Conditional statements that apply different styles based on device characteristics like screen size.',
          explanation: 'Media queries are essential for responsive design.'
        },
        {
          id: 'css-fc-4',
          question: 'What is the difference between display: none and visibility: hidden?',
          answer: 'display: none removes the element from the document flow; visibility: hidden hides it but keeps its space.',
          explanation: 'Elements with display: none don\'t take up space, while visibility: hidden elements are invisible but still occupy space.'
        },
        {
          id: 'css-fc-5',
          question: 'What is CSS specificity?',
          answer: 'A set of rules that determines which style declarations are applied to an element.',
          explanation: 'Specificity is calculated based on the type of selector (ID, class, element) and their quantity.'
        }
      ]
    };
  }
  
  // Default general knowledge flashcards
  else {
    return {
      id: `flashcards-general-${Date.now()}`,
      title: 'General Knowledge Flashcards',
      description: 'Review general knowledge concepts with these flashcards',
      cards: [
        {
          id: 'gen-fc-1',
          question: 'What is the capital of France?',
          answer: 'Paris',
          explanation: 'Paris is the capital and most populous city of France.'
        },
        {
          id: 'gen-fc-2',
          question: 'Who wrote "Romeo and Juliet"?',
          answer: 'William Shakespeare',
          explanation: 'William Shakespeare wrote this famous tragedy about star-crossed lovers around 1595.'
        },
        {
          id: 'gen-fc-3',
          question: 'What is the chemical symbol for gold?',
          answer: 'Au',
          explanation: 'The symbol Au comes from the Latin word for gold, "aurum".'
        },
        {
          id: 'gen-fc-4',
          question: 'Which planet is known as the Red Planet?',
          answer: 'Mars',
          explanation: 'Mars appears reddish because of iron oxide (rust) on its surface.'
        },
        {
          id: 'gen-fc-5',
          question: 'What is the largest ocean on Earth?',
          answer: 'Pacific Ocean',
          explanation: 'The Pacific Ocean covers more than 30% of the Earth\'s surface.'
        }
      ]
    };
  }
};

// Save quiz progress to localStorage
export const saveQuizProgress = (userId, quizId, progress) => {
  const key = userId ? `user_${userId}_quiz_${quizId}` : `anonymous_quiz_${quizId}`;
  localStorage.setItem(key, JSON.stringify(progress));
};

// Save flashcard progress to localStorage
export const saveFlashcardProgress = (userId, setId, progress) => {
  const key = userId ? `user_${userId}_flashcards_${setId}` : `anonymous_flashcards_${setId}`;
  localStorage.setItem(key, JSON.stringify(progress));
};

// Load quiz progress from localStorage
export const loadQuizProgress = (userId, quizId) => {
  const key = userId ? `user_${userId}_quiz_${quizId}` : `anonymous_quiz_${quizId}`;
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : null;
};

// Load flashcard progress from localStorage
export const loadFlashcardProgress = (userId, setId) => {
  const key = userId ? `user_${userId}_flashcards_${setId}` : `anonymous_flashcards_${setId}`;
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : null;
};
